import { ThemeEvents } from '@theme/events';

class LangleyCartEnhancements extends HTMLElement {
  static productCache = new Map();

  #abortController = new AbortController();
  #originButton = null;
  #product = null;
  #selectedOptions = [];
  #variantMap = {};
  #defaultImage = '';
  #defaultPrice = '';
  #statusTimer;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { capture: true, signal });

    this.#ensureRuntimeStyles();
    this.#enhanceQuantityInputs();
    this.#restorePendingStatus();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    if (this.#statusTimer) window.clearTimeout(this.#statusTimer);
  }

  get panel() {
    return this.querySelector('[data-langley-options-panel]');
  }

  get main() {
    return this.querySelector('[data-langley-cart-main]');
  }

  get panelFormComponent() {
    return this.querySelector('[data-langley-panel-form-component]');
  }

  get panelVariantInput() {
    return this.querySelector('[data-langley-panel-variant-id]');
  }

  get panelQuantityInput() {
    return this.querySelector('[data-langley-panel-form-component] input[name="quantity"]');
  }

  get panelAddButton() {
    return this.querySelector('[data-langley-panel-add]');
  }

  get panelError() {
    return this.querySelector('[data-langley-panel-error]');
  }

  get panelOptions() {
    return this.querySelector('[data-langley-panel-options]');
  }

  get panelPrice() {
    return this.querySelector('[data-langley-panel-price]');
  }

  get panelImage() {
    return this.querySelector('[data-langley-panel-image]');
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chooseButton = target.closest('[data-langley-choose-options]');
    if (chooseButton instanceof HTMLButtonElement && this.contains(chooseButton)) {
      event.preventDefault();
      this.#openOptionsPanel(chooseButton);
      return;
    }

    const closeButton = target.closest('[data-langley-options-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.#closeOptionsPanel(true);
      return;
    }

    const optionButton = target.closest('[data-langley-option-value]');
    if (optionButton instanceof HTMLButtonElement && this.panel?.contains(optionButton)) {
      event.preventDefault();
      this.#selectOption(optionButton);
      return;
    }

    const minusButton = target.closest('[data-langley-qty-minus]');
    if (minusButton instanceof HTMLButtonElement && this.contains(minusButton)) {
      event.preventDefault();
      this.#changeQuantity(minusButton, -1);
      return;
    }

    const plusButton = target.closest('[data-langley-qty-plus]');
    if (plusButton instanceof HTMLButtonElement && this.contains(plusButton)) {
      event.preventDefault();
      this.#changeQuantity(plusButton, 1);
    }
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-langley-quantity-input]')) return;
    this.#clampQuantity(target);
  };

  #onSubmit = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLFormElement)) return;

    const quantityInput = target.querySelector('[data-langley-quantity-input]');
    if (quantityInput instanceof HTMLInputElement) this.#clampQuantity(quantityInput);

    if (target.closest('[data-langley-panel-form-component]')) {
      this.#setPanelSubmitting(true);
      this.#setPanelError('');
      return;
    }

    const card = target.closest('.langley-complete-look__card');
    const message = card?.querySelector('[data-langley-card-message]');
    if (message) message.textContent = '';
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.panel?.hasAttribute('data-open')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#closeOptionsPanel(true);
  };

  #onCartUpdate = (event) => {
    const source = event.target;
    if (!(source instanceof Element) || !this.contains(source)) return;
    if (!source.closest('[data-langley-recommendation-form], [data-langley-panel-form-component]')) return;
    if (event.detail?.data?.didError) return;

    const drawer = this.closest('cart-drawer-component');
    if (drawer instanceof HTMLElement) drawer.dataset.langleyCartMessage = 'Added to your bag';

    this.#closeOptionsPanel(false);
  };

  #onCartError = (event) => {
    const source = event.target;
    if (!(source instanceof Element) || !this.contains(source)) return;

    const message =
      event.detail?.data?.message || event.detail?.data?.description || 'We could not add this item. Please try again.';

    if (source.closest('[data-langley-panel-form-component]')) {
      this.#setPanelError(message);
      this.#setPanelSubmitting(false);
      return;
    }

    const card = source.closest('.langley-complete-look__card');
    const messageElement = card?.querySelector('[data-langley-card-message]');
    if (messageElement) messageElement.textContent = message;
  };

  #ensureRuntimeStyles() {
    if (document.getElementById('langley-cart-enhancements-runtime-styles')) return;

    const style = document.createElement('style');
    style.id = 'langley-cart-enhancements-runtime-styles';
    style.textContent = `
      .langley-recommendation-qty {
        display: grid;
        gap: 7px;
        width: fit-content;
        margin-top: 8px;
      }
      .langley-recommendation-qty__label {
        color: var(--color-foreground);
        font-size: 14px;
        line-height: 1.25;
      }
      .langley-recommendation-qty__controls {
        display: grid;
        grid-template-columns: 44px minmax(48px, 58px) 44px;
        min-height: 44px;
        border: 1px solid rgb(var(--color-border-rgb) / 0.85);
        background: var(--color-background);
      }
      .langley-recommendation-qty__button {
        min-width: 44px;
        min-height: 44px;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--color-foreground);
        font: inherit;
        font-size: 20px;
        line-height: 1;
        cursor: pointer;
      }
      .langley-recommendation-qty__button:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
      .langley-recommendation-qty__input {
        width: 100%;
        min-width: 0;
        min-height: 44px;
        padding: 0 4px;
        border: 0;
        border-inline: 1px solid rgb(var(--color-border-rgb) / 0.65);
        border-radius: 0;
        background: transparent;
        color: var(--color-foreground);
        font: inherit;
        font-size: 14px;
        text-align: center;
        -moz-appearance: textfield;
      }
      .langley-recommendation-qty__input::-webkit-outer-spin-button,
      .langley-recommendation-qty__input::-webkit-inner-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }
      .langley-options-panel__footer .langley-recommendation-qty {
        width: 100%;
        margin: 0 0 12px;
      }
      .langley-options-panel__footer .langley-recommendation-qty__controls {
        grid-template-columns: 48px minmax(56px, 1fr) 48px;
        width: 100%;
      }
      .langley-complete-look__card .langley-recommendation-qty {
        margin-bottom: 2px;
      }
    `;
    document.head.append(style);
  }

  #enhanceQuantityInputs() {
    const inputs = this.querySelectorAll(
      '[data-langley-recommendation-form] input[name="quantity"], [data-langley-panel-form-component] input[name="quantity"]'
    );

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.dataset.langleyQuantityEnhanced === 'true') return;
      this.#createQuantityControl(input);
    });
  }

  #createQuantityControl(input) {
    const form = input.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = form.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    input.dataset.langleyQuantityEnhanced = 'true';
    input.dataset.langleyQuantityInput = '';
    input.type = 'number';
    input.className = 'langley-recommendation-qty__input';
    input.inputMode = 'numeric';

    const currentValue = Math.max(1, Number(input.value) || 1);
    if (!input.min) input.min = String(currentValue);
    if (!input.step) input.step = '1';
    input.value = String(currentValue);

    const wrapper = document.createElement('div');
    wrapper.className = 'langley-recommendation-qty';
    wrapper.dataset.langleyQuantityControl = '';

    const label = document.createElement('span');
    label.className = 'langley-recommendation-qty__label';
    label.textContent = 'Quantity';

    const controls = document.createElement('div');
    controls.className = 'langley-recommendation-qty__controls';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'langley-recommendation-qty__button';
    minus.dataset.langleyQtyMinus = '';
    minus.setAttribute('aria-label', 'Decrease quantity');
    minus.textContent = '−';

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'langley-recommendation-qty__button';
    plus.dataset.langleyQtyPlus = '';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.textContent = '+';

    form.insertBefore(wrapper, submitButton);
    wrapper.append(label, controls);
    controls.append(minus, input, plus);

    this.#clampQuantity(input);
  }

  #changeQuantity(button, direction) {
    const control = button.closest('[data-langley-quantity-control]');
    const input = control?.querySelector('[data-langley-quantity-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const step = Math.max(1, Number(input.step) || 1);
    const min = Math.max(1, Number(input.min) || 1);
    const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const current = Number(input.value) || min;
    const next = direction > 0 ? current + step : current - step;

    input.value = String(Math.min(max, Math.max(min, next)));
    this.#clampQuantity(input);
  }

  #clampQuantity(input) {
    const step = Math.max(1, Number(input.step) || 1);
    const min = Math.max(1, Number(input.min) || 1);
    const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    let value = Number(input.value);

    if (!Number.isFinite(value)) value = min;
    value = Math.max(min, Math.min(max, value));
    value = min + Math.round((value - min) / step) * step;
    value = Math.max(min, Math.min(max, value));
    input.value = String(value);

    const control = input.closest('[data-langley-quantity-control]');
    const minus = control?.querySelector('[data-langley-qty-minus]');
    const plus = control?.querySelector('[data-langley-qty-plus]');
    if (minus instanceof HTMLButtonElement) minus.disabled = value <= min;
    if (plus instanceof HTMLButtonElement) plus.disabled = Number.isFinite(max) && value >= max;
  }

  #applyPanelQuantityRule(variant) {
    const input = this.panelQuantityInput;
    if (!(input instanceof HTMLInputElement)) return;

    const rule = variant?.quantity_rule || variant?.quantityRule || {};
    const min = Math.max(1, Number(rule.min) || 1);
    const step = Math.max(1, Number(rule.increment) || 1);
    const max = Number(rule.max);

    input.min = String(min);
    input.step = String(step);
    if (Number.isFinite(max) && max > 0) input.max = String(max);
    else input.removeAttribute('max');

    const current = Number(input.value);
    input.value = String(Number.isFinite(current) && current >= min ? current : min);
    this.#clampQuantity(input);
  }

  #restorePendingStatus() {
    const drawer = this.closest('cart-drawer-component');
    const message = drawer instanceof HTMLElement ? drawer.dataset.langleyCartMessage : '';
    if (!message) return;

    delete drawer.dataset.langleyCartMessage;
    const status = this.querySelector('[data-langley-cart-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = message;
    status.removeAttribute('hidden');
    if (this.#statusTimer) window.clearTimeout(this.#statusTimer);
    this.#statusTimer = window.setTimeout(() => {
      status.textContent = '';
      status.setAttribute('hidden', '');
    }, 2500);
  }

  async #openOptionsPanel(button) {
    const panel = this.panel;
    const main = this.main;
    if (!(panel instanceof HTMLElement) || !(main instanceof HTMLElement)) return;

    this.#originButton = button;
    this.#product = null;
    this.#selectedOptions = [];
    this.#variantMap = this.#readVariantMap(button);
    this.#defaultImage = button.dataset.productImage || '';
    this.#defaultPrice = button.dataset.productPrice || '';

    this.#resetPanel(button);
    main.inert = true;
    panel.inert = false;
    panel.setAttribute('data-open', '');
    panel.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      const focusTarget = panel.querySelector('[data-langley-options-close]');
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
    });

    try {
      const rawUrl = button.dataset.productJsonUrl;
      if (!rawUrl) throw new Error('Missing product data URL');

      const product = await this.#fetchProduct(rawUrl);
      if (this.#originButton !== button || !panel.hasAttribute('data-open')) return;

      this.#product = product;
      const options = this.#normalizeOptions(product);
      this.#selectedOptions = new Array(options.length).fill(null);
      this.#renderOptions();
    } catch (error) {
      console.error('Langley cart options error:', error);
      if (this.#originButton !== button) return;
      this.#setPanelError('Options could not be loaded. Please try again.');
      if (this.panelOptions) this.panelOptions.textContent = '';
    }
  }

  #closeOptionsPanel(returnFocus) {
    const panel = this.panel;
    const main = this.main;
    if (!(panel instanceof HTMLElement) || !panel.hasAttribute('data-open')) return;

    panel.removeAttribute('data-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
    if (main instanceof HTMLElement) main.inert = false;

    const origin = this.#originButton;
    this.#originButton = null;
    this.#product = null;
    this.#selectedOptions = [];
    this.#variantMap = {};
    this.#setPanelSubmitting(false);

    if (returnFocus && origin instanceof HTMLElement && origin.isConnected) {
      requestAnimationFrame(() => origin.focus());
    }
  }

  #resetPanel(button) {
    const title = this.querySelector('[data-langley-panel-title]');
    const price = this.panelPrice;
    const image = this.panelImage;
    const options = this.panelOptions;
    const input = this.panelVariantInput;
    const formComponent = this.panelFormComponent;
    const quantityInput = this.panelQuantityInput;

    if (title) title.textContent = button.dataset.productTitle || '';
    if (price) price.textContent = this.#defaultPrice;
    if (image instanceof HTMLImageElement) {
      image.toggleAttribute('hidden', !this.#defaultImage);
      if (this.#defaultImage) image.src = this.#defaultImage;
      image.alt = button.dataset.productTitle || '';
    }
    if (options) options.textContent = 'Loading options…';
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.disabled = true;
    }
    if (formComponent instanceof HTMLElement) formComponent.dataset.productId = button.dataset.productId || '';
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.min = '1';
      quantityInput.step = '1';
      quantityInput.removeAttribute('max');
      quantityInput.value = '1';
      this.#clampQuantity(quantityInput);
    }

    this.#setPanelError('');
    this.#setPanelSubmitting(false);
    this.#updateAddButton(null);
  }

  #readVariantMap(button) {
    const card = button.closest('.langley-complete-look__card');
    const script = card?.querySelector('[data-langley-variant-map]');
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('Invalid Langley variant map:', error);
      return {};
    }
  }

  #normalizeProductJsonUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\.js$/i, '').replace(/\/$/, '')}.js`;
    return url.toString();
  }

  #fetchProduct(rawUrl) {
    const url = this.#normalizeProductJsonUrl(rawUrl);

    if (!LangleyCartEnhancements.productCache.has(url)) {
      const request = fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Product request failed (${response.status})`);
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('json')) throw new Error('Product endpoint did not return JSON');
        return response.json();
      });

      LangleyCartEnhancements.productCache.set(url, request);
      request.catch(() => LangleyCartEnhancements.productCache.delete(url));
    }

    return LangleyCartEnhancements.productCache.get(url);
  }

  #normalizeOptions(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    let optionCount = 0;

    if (Array.isArray(product?.options)) optionCount = product.options.length;
    if (!optionCount && variants[0]) optionCount = this.#variantValues(variants[0]).length;

    return Array.from({ length: optionCount }, (_, index) => {
      const rawOption = product.options?.[index];
      const name = typeof rawOption === 'string' ? rawOption : rawOption?.name || `Option ${index + 1}`;
      const rawValues = typeof rawOption === 'object' && Array.isArray(rawOption?.values) ? rawOption.values : [];
      const suppliedValues = rawValues
        .map((value) => (typeof value === 'string' ? value : value?.name ?? value?.value ?? ''))
        .filter(Boolean);
      const derivedValues = variants.map((variant) => this.#variantValues(variant)[index]).filter(Boolean);

      return {
        name,
        values: [...new Set(suppliedValues.length ? suppliedValues : derivedValues)],
      };
    });
  }

  #variantValues(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map((value) => (typeof value === 'string' ? value : value?.name ?? value?.value ?? String(value)));
    }

    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  }

  #renderOptions() {
    const container = this.panelOptions;
    if (!(container instanceof HTMLElement) || !this.#product) return;

    const options = this.#normalizeOptions(this.#product);
    container.textContent = '';

    if (!options.length) {
      const emptyState = document.createElement('p');
      emptyState.className = 'langley-options-panel__error';
      emptyState.textContent = 'No selectable options were returned for this product.';
      container.append(emptyState);
      this.#syncVariant();
      return;
    }

    options.forEach((option, optionIndex) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'langley-options-panel__fieldset';

      const legend = document.createElement('legend');
      legend.className = 'langley-options-panel__legend';
      legend.textContent = option.name;
      fieldset.append(legend);

      const values = document.createElement('div');
      values.className = 'langley-options-panel__values';

      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'langley-options-panel__option';
        button.dataset.langleyOptionValue = value;
        button.dataset.optionIndex = String(optionIndex);
        button.setAttribute('aria-pressed', this.#selectedOptions[optionIndex] === value ? 'true' : 'false');
        button.textContent = value;
        button.disabled = !this.#hasAvailableVariant(optionIndex, value);
        values.append(button);
      });

      fieldset.append(values);
      container.append(fieldset);
    });

    this.#syncVariant();
  }

  #selectOption(button) {
    const optionIndex = Number(button.dataset.optionIndex);
    const value = button.dataset.langleyOptionValue;
    if (!Number.isInteger(optionIndex) || value == null || button.disabled) return;

    this.#selectedOptions[optionIndex] = value;
    this.#setPanelError('');
    this.#renderOptions();

    const selectedButton = [...(this.panelOptions?.querySelectorAll('[data-langley-option-value]') || [])].find(
      (candidate) =>
        candidate instanceof HTMLButtonElement &&
        Number(candidate.dataset.optionIndex) === optionIndex &&
        candidate.dataset.langleyOptionValue === value
    );
    if (selectedButton instanceof HTMLElement) selectedButton.focus();
  }

  #hasAvailableVariant(optionIndex, candidateValue) {
    if (!this.#product?.variants) return false;

    return this.#product.variants.some((variant) => {
      if (!variant.available) return false;
      const values = this.#variantValues(variant);

      return this.#selectedOptions.every((selection, index) => {
        const expected = index === optionIndex ? candidateValue : selection;
        return !expected || values[index] === expected;
      });
    });
  }

  #findSelectedVariant() {
    if (!this.#product?.variants || this.#selectedOptions.some((value) => !value)) return null;

    return (
      this.#product.variants.find((variant) => {
        const values = this.#variantValues(variant);
        return this.#selectedOptions.every((value, index) => values[index] === value);
      }) || null
    );
  }

  #syncVariant() {
    const variant = this.#findSelectedVariant();
    const input = this.panelVariantInput;

    if (input instanceof HTMLInputElement) {
      input.value = variant?.available ? String(variant.id) : '';
      input.disabled = !variant?.available;
    }

    this.#applyPanelQuantityRule(variant);
    this.#updateAddButton(variant);
    this.#updatePanelMedia(variant);
  }

  #updateAddButton(variant) {
    const button = this.panelAddButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const valid = Boolean(variant?.available);
    button.disabled = !valid;
    button.textContent = valid ? 'Add to Bag' : 'Select options';
  }

  #updatePanelMedia(variant) {
    const details = variant ? this.#variantMap[String(variant.id)] : null;
    const price = this.panelPrice;
    const image = this.panelImage;

    if (price) price.textContent = details?.price || this.#defaultPrice;
    if (image instanceof HTMLImageElement) {
      const nextImage = details?.image || this.#defaultImage;
      image.toggleAttribute('hidden', !nextImage);
      if (nextImage) image.src = nextImage;
    }
  }

  #setPanelSubmitting(isSubmitting) {
    const button = this.panelAddButton;
    if (!(button instanceof HTMLButtonElement)) return;

    if (isSubmitting) {
      button.disabled = true;
      button.textContent = 'Adding…';
    } else {
      this.#updateAddButton(this.#findSelectedVariant());
    }
  }

  #setPanelError(message) {
    const error = this.panelError;
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message || '';
    error.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('langley-cart-enhancements')) {
  customElements.define('langley-cart-enhancements', LangleyCartEnhancements);
}
